import type { MaybeRefOrGetter } from 'vue';
import { computed, toValue } from 'vue';
import type { ManualTrainingsResponse } from '~/types/academy/manualTrainings';
import { withResolvedCtaUrl, withTeaserColor } from '~/utils/academy/trainings/enrichTrainingsListingItem';
import { ACADEMY_AGGREGATED_REQUEST_TIMEOUT, ACADEMY_TRAININGS_MANUAL_KEY_PREFIX } from '~/utils/academy/constants';

export function useAcademyManualTrainings(courseIds: MaybeRefOrGetter<number[]>) {
    const localizedPath = useLocalizedPath();
    const academyPath = useRequiredSectionPathById('academy');
    const { urlSegment } = useLanguage();
    const auth = useAuth();
    const isAuthenticated = computed(() => Boolean(auth.value));
    const serializedCourseIds = computed(() => toValue(courseIds).join(','));
    const hasCourseIds = computed(() => serializedCourseIds.value !== '');
    const canFetch = computed(() => isAuthenticated.value && hasCourseIds.value);

    // Keyed by the serialized IDs, not the array identities replaced by bridge updates.
    const { data, error, status } = useAsyncData(
        () => `${ACADEMY_TRAININGS_MANUAL_KEY_PREFIX}:${serializedCourseIds.value}:${auth.value?.sub ?? 'anonymous'}`,
        (_nuxtApp, { signal }) => {
            return $fetch<ManualTrainingsResponse>('/api/academy/trainings/manual', {
                credentials: 'include',
                query: { course_ids: serializedCourseIds.value },
                signal
            });
        },
        {
            server: false,
            enabled: canFetch,
            timeout: ACADEMY_AGGREGATED_REQUEST_TIMEOUT
        }
    );

    const items = computed(() =>
        (canFetch.value ? (data.value?.items ?? []) : [])
            .map(withTeaserColor)
            .map((item) => withResolvedCtaUrl(item, localizedPath, urlSegment.value, academyPath.value))
    );
    const prettyError = computed(() => (error.value ? JSON.stringify(error.value, null, 2) : null));
    // Client-only requests are idle during server rendering, before the browser starts the request.
    const isInitialLoading = computed(
        () => canFetch.value && (status.value === 'idle' || status.value === 'pending') && items.value.length === 0
    );
    const showNoResults = computed(() => canFetch.value && status.value === 'success' && items.value.length === 0);

    return {
        isAuthenticated,
        hasCourseIds,
        canFetch,
        prettyError,
        isInitialLoading,
        showNoResults,
        items
    };
}
